import { useState } from "react";
import { CameraCapture } from "@/components/CameraCapture";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Camera } from "lucide-react";
import { toast } from "sonner";

export function FieldRecheck({ field, open, onOpenChange, onDone }) {
  const { user } = useAuth();
  const [photo, setPhoto] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!photo) return toast.error("Take or upload a soil photo first");
    setBusy(true);
    try {
      const res = await apiClient.post(`/fields/${field.id}/recheck`, { image_base64: photo.base64, mime_type: photo.mime, notes, language: user?.language || "English" });
      onDone(res.data);
      toast.success(`${field.name} updated — new snapshot added to its trend`);
      setPhoto(null); setNotes("");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent data-testid="recheck-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Recheck {field?.name} with a photo</DialogTitle>
          <DialogDescription>Snap today's soil. The AI compares it with your last record and adds a fresh point to the soil trend.</DialogDescription>
        </DialogHeader>
        <CameraCapture photo={photo} onPhoto={setPhoto} />
        <div>
          <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">What changed? (optional)</Label>
          <Input data-testid="recheck-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. added compost last month, heavy rains" className="mt-1.5 bg-white" />
        </div>
        <Button data-testid="recheck-submit-btn" onClick={run} disabled={busy || !photo} className="h-12 bg-[#D97706] hover:bg-[#78350F] text-white rounded-xl font-semibold">
          {busy ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Comparing with last record…</> : <><Camera className="h-5 w-5 mr-2" /> Analyse & add to trend</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
